import { Box, Image, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { asHTML, asText } from "@prismicio/client";
import { ACCENT, HERO_BG, HOME_HERO_ORBS, heroGradientBoxProps } from "./heroBackground";

// Matches the website's Processimage slice exactly (antaia-website
// src/slices/Processimage) — dark hero-gradient background, lime lozenges, white
// headings/body text on the process-block cards.
const LOZENGE_BG = `${ACCENT}1A`;

function Lozenge({ text, uppercase }) {
  return (
    <Text
      display="inline-block"
      px={3}
      py={1.5}
      fontSize={uppercase ? "11px" : "12px"}
      fontWeight={uppercase ? "700" : "500"}
      letterSpacing={uppercase ? "wide" : undefined}
      textTransform={uppercase ? "uppercase" : undefined}
      bg={LOZENGE_BG}
      color={ACCENT}
      borderRadius="full"
    >
      {text}
    </Text>
  );
}

const BLOCK_HEADING_SX = { p: { fontWeight: "700", fontSize: "body.l", color: "white", m: 0 }, strong: { color: ACCENT } };
const BLOCK_BODY_SX = {
  p: { fontSize: "body.m", color: "whiteAlpha.700", lineHeight: "1.6", m: 0 },
  // Prismic rich text renders bullet lists with browser defaults otherwise — near-invisible
  // dark text on this slice's dark gradient background. White + bold reads as clearly
  // authored content, not paragraph filler; mt separates the list from a preceding
  // paragraph, li's mb gives each point breathing room without the list feeling loose.
  ul: { pl: 5, mt: 2, mb: 0 },
  li: { fontSize: "body.m", color: "white", fontWeight: "700", lineHeight: "1.6", mb: 2, "&:last-of-type": { mb: 0 } },
};
const INTRO_HEADING_SX = { p: { fontSize: { base: "28px", md: "36px" }, fontWeight: "700", color: "white", m: 0, lineHeight: "1.3" }, strong: { color: ACCENT } };
const INTRO_SUB_HEADING_SX = { p: { fontSize: "body.l", color: ACCENT, fontWeight: "600", m: 0 } };
const INTRO_BODY_SX = { p: { fontSize: "body.m", color: "whiteAlpha.800", lineHeight: "1.7", m: 0 } };

export default function ProcessimageSlice({ slice }) {
  const { lozenge, heading, sub_heading, body, process_blocks, image } = slice.primary;
  const hasImage = !!image?.url;

  return (
    <Box {...heroGradientBoxProps} py={{ base: "64px", md: "96px" }} px={6}>
      {HOME_HERO_ORBS.map((orbProps, i) => (
        <Box key={i} {...orbProps} />
      ))}
      <VStack position="relative" zIndex={1} align="stretch" gap={10} maxW="1100px" mx="auto">
        {(lozenge || heading?.length > 0 || sub_heading?.length > 0 || body?.length > 0) && (
          <VStack align="center" textAlign="center" gap={2} maxW="700px" mx="auto">
            {lozenge && <Lozenge text={lozenge} />}
            {heading?.length > 0 && <Box sx={INTRO_HEADING_SX} dangerouslySetInnerHTML={{ __html: asHTML(heading) }} />}
            {sub_heading?.length > 0 && <Box sx={INTRO_SUB_HEADING_SX} dangerouslySetInnerHTML={{ __html: asHTML(sub_heading) }} />}
            {body?.length > 0 && <Box sx={INTRO_BODY_SX} dangerouslySetInnerHTML={{ __html: asHTML(body) }} />}
          </VStack>
        )}

        <SimpleGrid columns={{ base: 1, lg: hasImage ? 2 : 1 }} gap={10} alignItems="center">
          {process_blocks?.length > 0 && (
            <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
              {process_blocks.map((block, index) => (
                <Box
                  key={index}
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  borderRadius="xl"
                  p={5}
                  _hover={{ borderColor: `${ACCENT}4D` }}
                  transition="border-color 0.15s"
                >
                  <VStack align="start" gap={3}>
                    {block.lozenge && <Lozenge text={block.lozenge} uppercase />}
                    {block.heading?.length > 0 && (
                      <Box sx={BLOCK_HEADING_SX} dangerouslySetInnerHTML={{ __html: asHTML(block.heading) }} />
                    )}
                    {block.body?.length > 0 && <Box sx={BLOCK_BODY_SX} dangerouslySetInnerHTML={{ __html: asHTML(block.body) }} />}
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          )}

          {hasImage && (
            <Box
              borderRadius="xl"
              overflow="hidden"
              border="1px solid"
              borderColor={`${ACCENT}33`}
              bg="whiteAlpha.100"
              p={1}
            >
              <Image src={image.url} alt={image.alt || asText(heading) || ""} w="100%" borderRadius="lg" />
            </Box>
          )}
        </SimpleGrid>
      </VStack>
    </Box>
  );
}
